import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AppLayout from '../../components/AppLayout';
import { S, Icon, PageHeader, DataCard, BtnPrimary, BtnSecondary, TH, TD } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// ─── Status indicator colors (label resolved via t at render) ────
const STATUS_COLORS = {
  verified: { bg: '#dcfce7', color: '#15803d', dot: '#10b981', labelKey: 'screens.integrity.statusVerified' },
  stale:    { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', labelKey: 'screens.integrity.statusStale' },
  outdated: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', labelKey: 'screens.integrity.statusOutdated' },
  no_data:  { bg: '#f1f5f9', color: '#64748b', dot: '#94a3b8', labelKey: 'screens.integrity.statusNoData' },
};

const ANCHOR_STATUS_BADGE = {
  anchored: { bg: '#dcfce7', color: '#15803d', labelKey: 'screens.integrity.anchorAnchored' },
  pending:  { bg: '#fef3c7', color: '#92400e', labelKey: 'screens.integrity.anchorPending' },
  failed:   { bg: '#fee2e2', color: '#991b1b', labelKey: 'screens.integrity.anchorFailed' },
};

// ─── Relative time formatter ─────────────────────────────
function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatTimestamp(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
}

function truncateHash(hash) {
  if (!hash) return '—';
  if (hash.length <= 14) return hash;
  return hash.slice(0, 8) + '...' + hash.slice(-6);
}

// ═══════════════════════════════════════════════════════════
// AUDIT INTEGRITY SCREEN
// ═══════════════════════════════════════════════════════════
const AuditIntegrityScreen = () => {
  const { t } = useTranslation();
  const userLogin = useSelector(state => state.userLogin);
  const token = userLogin?.userInfo?.token || userLogin?.userInfo?.data?.token;

  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifyState, setVerifyState] = useState({ running: false, result: null });
  // Per-row verify state — keyed by row.date. Shape: { running, ok, message }.
  const [rowVerify, setRowVerify] = useState({});

  const config = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  }), [token]);

  const verifyChain = useCallback(async () => {
    setVerifyState({ running: true, result: null });
    try {
      const resp = await axios.get(`${BASE_URL}/audit/verify?limit=1000`, config());
      const { valid, checked, firstTamperedIndex } = resp.data?.data || {};
      setVerifyState({
        running: false,
        result: valid
          ? { ok: true, message: t('screens.integrity.chainVerifiedMsg', { count: checked || 0 }) }
          : { ok: false, message: t('screens.integrity.tamperDetectedMsg', { index: firstTamperedIndex }) },
      });
    } catch (err) {
      setVerifyState({ running: false, result: { ok: false, message: err.response?.data?.message || t('screens.integrity.verifyFailed') } });
    }
  }, [config, t]);

  // Verify the log chain for a single anchor's date. Uses the same endpoint
  // as the global verify but scoped to a one-day window. S26 test expects
  // this button to sit on every row of the anchor history table.
  const verifyRow = useCallback(async (rowDate) => {
    if (!rowDate) return;
    setRowVerify(prev => ({ ...prev, [rowDate]: { running: true } }));
    try {
      const resp = await axios.get(
        `${BASE_URL}/audit/verify?date=${encodeURIComponent(rowDate)}&limit=1000`,
        config(),
      );
      const { valid, checked, firstTamperedIndex } = resp.data?.data || {};
      const message = valid
        ? t('screens.integrity.chainVerifiedMsg', { count: checked || 0 })
        : t('screens.integrity.tamperDetectedMsg', { index: firstTamperedIndex });
      setRowVerify(prev => ({ ...prev, [rowDate]: { running: false, ok: !!valid, message } }));
    } catch (err) {
      setRowVerify(prev => ({
        ...prev,
        [rowDate]: {
          running: false,
          ok: false,
          message: err.response?.data?.message || t('screens.integrity.verifyFailed'),
        },
      }));
    }
  }, [config, t]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${BASE_URL}/audit/integrity/status`, config()),
        axios.get(`${BASE_URL}/audit/integrity/history?limit=30`, config()),
      ]);
      setStatus(statusRes.data.data);
      setHistory(historyRes.data.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('screens.integrity.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [config, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownloadProof = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/audit/integrity/latest-proof`, {
        ...config(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'audit-proof-latest.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(t('screens.integrity.downloadProofFailed') + ': ' + (err.response?.data?.message || err.message));
    }
  };

  const statusCfg = STATUS_COLORS[status?.verificationStatus] || STATUS_COLORS.no_data;

  return (
    <AppLayout>
      <PageHeader
        breadcrumb={<><span>{t('screens.integrity.breadcrumbCompliance')}</span> <Icon name="chevronRight" size={10} /> <span>{t('screens.integrity.breadcrumbAuditIntegrity')}</span></>}
        title={t('screens.integrity.title')}
        actions={
          <BtnSecondary onClick={fetchData} style={{ gap: 6 }}>
            <Icon name="refresh" color={S.textSecondary} size={14} />
            {t('screens.integrity.refresh')}
          </BtnSecondary>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto 12px' }} />
            <div style={{ fontSize: 13, color: S.textMuted }}>{t('screens.integrity.loadingStatus')}</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : error ? (
          <DataCard style={{ textAlign: 'center', padding: 60 }}>
            <Icon name="alert-circle" color={S.danger} size={32} />
            <div style={{ fontSize: 15, fontWeight: 600, color: S.textPrimary, marginTop: 12 }}>{t('screens.integrity.failedLoad')}</div>
            <div style={{ fontSize: 13, color: S.textMuted, marginTop: 4 }}>{error}</div>
            <BtnSecondary onClick={fetchData} style={{ marginTop: 16, display: 'inline-flex' }}>{t('screens.integrity.retry')}</BtnSecondary>
          </DataCard>
        ) : (
          <>
            {/* ── Status Banner ────────────────────────────── */}
            <div style={{
              background: '#fff',
              border: `1px solid ${S.borderLight}`,
              borderLeft: `4px solid ${S.burgundy}`,
              borderRadius: 14,
              padding: '20px 24px',
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: S.textPrimary }}>{t('screens.integrity.statusHeading')}</div>
                <div style={{ fontSize: 13, color: S.textMuted, marginTop: 2 }}>
                  {t('screens.integrity.statusSubheading')}
                </div>
              </div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '6px 16px', borderRadius: 20,
                background: statusCfg.bg, color: statusCfg.color,
                fontSize: 13, fontWeight: 700,
              }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusCfg.dot }} />
                {t(statusCfg.labelKey)}
              </div>
            </div>

            {/* ── Stats Row ────────────────────────────────── */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 16,
              marginBottom: 24,
            }}>
              <StatCard
                label={t('screens.integrity.lastAnchored')}
                value={status?.lastAnchoredAt ? timeAgo(status.lastAnchoredAt) : '—'}
                sub={formatTimestamp(status?.lastAnchoredAt)}
                icon="clock"
                color={S.burgundy}
              />
              <StatCard
                label={t('screens.integrity.anchoredDays')}
                value={status?.totalAnchoredDays ?? 0}
                sub={t('screens.integrity.anchoredDaysSub')}
                icon="calendar"
                color={S.info}
              />
              <StatCard
                label={t('screens.integrity.auditEventsAnchored')}
                value={(status?.totalAuditEvents ?? 0).toLocaleString()}
                sub={t('screens.integrity.auditEventsAnchoredSub')}
                icon="shield"
                color={S.success}
              />
              <StatCard
                label={t('screens.integrity.anchorStorage')}
                value="AWS S3"
                sub={t('screens.integrity.anchorStorageSub')}
                icon="lock"
                color="#8b5cf6"
              />
            </div>

            {/* ── Anchor History Table ─────────────────────── */}
            <DataCard style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${S.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('screens.integrity.anchorHistory')}</div>
                  <div style={{ fontSize: 12, color: S.textMuted }}>{t('screens.integrity.anchorHistorySub')}</div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {verifyState.result && (
                    <span style={{
                      fontSize: 12, fontWeight: 600,
                      padding: '4px 10px', borderRadius: 6,
                      background: verifyState.result.ok ? '#dcfce7' : '#fee2e2',
                      color:      verifyState.result.ok ? '#15803d' : '#991b1b',
                    }}>
                      {verifyState.result.message}
                    </span>
                  )}
                  <BtnSecondary onClick={verifyChain} disabled={verifyState.running}>
                    <Icon name='shield' size={14} /> {verifyState.running ? t('screens.integrity.verifying') : t('screens.integrity.verifyChain')}
                  </BtnSecondary>
                </div>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: S.bgCard }}>
                      <TH>{t('screens.integrity.colDate')}</TH>
                      <TH>{t('screens.integrity.colRootHash')}</TH>
                      <TH>{t('screens.integrity.colLeafCount')}</TH>
                      <TH>{t('screens.integrity.colStatus')}</TH>
                      <TH>{t('screens.integrity.colAnchoredAt')}</TH>
                      <TH>{t('screens.integrity.colAnchorReference')}</TH>
                      <TH>{t('screens.integrity.colVerify')}</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {history.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: S.textMuted, fontSize: 13 }}>
                          {t('screens.integrity.noAnchorHistory')}
                        </td>
                      </tr>
                    ) : history.map((row, i) => {
                      const badge = ANCHOR_STATUS_BADGE[row.anchorStatus] || ANCHOR_STATUS_BADGE.pending;
                      return (
                        <tr
                          key={row.date}
                          style={{
                            borderBottom: `1px solid ${S.borderLight}`,
                            transition: 'background 0.1s',
                            cursor: 'default',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = S.bgHover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <TD style={{ fontWeight: 600, color: S.textPrimary }}>{row.date}</TD>
                          <TD>
                            <code style={{ fontSize: 12, background: S.bgCard, padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace' }}>
                              {truncateHash(row.rootHash)}
                            </code>
                          </TD>
                          <TD>{row.leafCount}</TD>
                          <TD>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '3px 10px', borderRadius: 12,
                              fontSize: 11, fontWeight: 600,
                              background: badge.bg, color: badge.color,
                            }}>
                              <span style={{ width: 5, height: 5, borderRadius: '50%', background: badge.color }} />
                              {t(badge.labelKey)}
                            </span>
                          </TD>
                          <TD>{row.anchoredAt ? formatTimestamp(row.anchoredAt) : '—'}</TD>
                          <TD>
                            {row.anchorReference ? (
                              <code style={{ fontSize: 11, color: S.textMuted, fontFamily: 'monospace' }}>
                                {row.anchorReference.length > 40
                                  ? row.anchorReference.slice(0, 40) + '...'
                                  : row.anchorReference}
                              </code>
                            ) : '—'}
                          </TD>
                          <TD>
                            {(() => {
                              const rs = rowVerify[row.date];
                              if (rs?.running) {
                                return (
                                  <span style={{ fontSize: 12, color: S.textMuted }}>
                                    {t('screens.integrity.verifying')}
                                  </span>
                                );
                              }
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <button
                                    type='button'
                                    onClick={() => verifyRow(row.date)}
                                    style={{
                                      padding: '4px 10px',
                                      background: 'transparent',
                                      border: `1px solid ${S.borderLight}`,
                                      borderRadius: 6,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: S.textSecondary,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {t('screens.integrity.verifyChain')}
                                  </button>
                                  {rs?.message && (
                                    <span style={{
                                      fontSize: 11, fontWeight: 600,
                                      padding: '2px 8px', borderRadius: 6,
                                      background: rs.ok ? '#dcfce7' : '#fee2e2',
                                      color:      rs.ok ? '#15803d' : '#991b1b',
                                    }}>
                                      {rs.message}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </TD>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </DataCard>

            {/* ── Verification Tools Card ──────────────────── */}
            <DataCard style={{ background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: S.burgundy + '14',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon name="shield" color={S.burgundy} size={22} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>
                    {t('screens.integrity.independentVerification')}
                  </div>
                  <div style={{ fontSize: 13, color: S.textMuted, lineHeight: 1.5, marginBottom: 16 }}>
                    {t('screens.integrity.independentVerificationDesc')}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <BtnSecondary onClick={() => {
                      const apiBase = BASE_URL.replace('/api/v1', '');
                      window.open(apiBase + '/api/v1/openapi.yaml', '_blank');
                    }}>
                      <Icon name="file-text" color={S.textSecondary} size={14} />
                      {t('screens.integrity.viewApiDocs')}
                    </BtnSecondary>
                    <BtnPrimary onClick={handleDownloadProof}>
                      <Icon name="download" color="#fff" size={14} />
                      {t('screens.integrity.downloadLatestProof')}
                    </BtnPrimary>
                  </div>
                </div>
              </div>
            </DataCard>
          </>
        )}
      </div>
    </AppLayout>
  );
};

// ─── Stat Card Sub-Component ─────────────────────────────
const StatCard = ({ label, value, sub, icon, color }) => (
  <div style={{
    background: '#fff',
    border: `1px solid ${S.borderLight}`,
    borderRadius: 16,
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </div>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: (color || S.burgundy) + '14',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} color={color || S.burgundy} size={18} />
      </div>
    </div>
    <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: 12, color: S.textMuted }}>{sub}</div>
  </div>
);

export default AuditIntegrityScreen;
